import type { ReactNode } from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  LiveAvatarSession,
  SessionEvent,
  SessionState,
  AgentEventsEnum,
} from '@heygen/liveavatar-web-sdk';
import type { TTSConfig } from '@trinity/chat/src/types/tts-config';
import { ChatLoggerContext } from '../../containers/Chat/contexts.js';
import { LiveAvatarContext } from './LiveAvatar.context';
import { useSessionKeepAlive } from './useSessionKeepAlive';

type Props = {
  /**
   * Session token returned by POST /v1/chat/live-avatar/session.
   * A new session is created (and the old one stopped) whenever this changes.
   */
  sessionToken: string;
  ttsConfig: TTSConfig | null;
  children: ReactNode;
};

/**
 * LiveAvatarContextProvider
 *
 * Creates and manages the lifecycle of a single LiveAvatarSession in LITE mode
 * (voiceChat: false — avatar lip-syncs to audio we provide via repeatAudio,
 * rather than using HeyGen's built-in TTS/voice pipeline).
 *
 * Lifecycle:
 *   1. On mount (or when sessionToken changes): create session, attach event
 *      listeners, call session.start().
 *   2. On unmount (or sessionToken change): set ref to null, call session.stop().
 *
 * Mount this provider ABOVE both the <LiveAvatar> video element and the
 * <HeyGenResponseBridge> so both children can access sessionRef.
 */
export function LiveAvatarContextProvider({ sessionToken, ttsConfig, children }: Props): React.ReactElement {
  const loggerCtx = useContext(ChatLoggerContext);
  const loggerRef = useRef(loggerCtx);
  loggerRef.current = loggerCtx;
  const sessionRef = useRef<LiveAvatarSession | null>(null);
  const [sessionState, setSessionState] = useState<SessionState>(SessionState.INACTIVE);
  const [isStreamReady, setIsStreamReady] = useState(false);
  const [isAvatarTalking, setIsAvatarTalking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  // Shared ref for paced TTS segments scheduled by HeyGenResponseBridge.
  // AvatarControlBar reads this on interrupt to cancel all queued segments.
  const pendingAudioTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Keep the HeyGen session alive with 30-second pings while the user is active.
  // Pinging stops automatically after 5 minutes of inactivity.
  useSessionKeepAlive(sessionRef, sessionState);

  useEffect(() => {
    // LITE mode: voiceChat: false disables HeyGen's built-in voice input/TTS.
    // We drive the avatar with session.repeatAudio(base64Audio) instead.
    const session = new LiveAvatarSession(sessionToken, { voiceChat: false });
    sessionRef.current = session;

    session.on(SessionEvent.SESSION_STATE_CHANGED, (state: SessionState) => {
      setSessionState(state);
    });

    // SESSION_STREAM_READY fires when the LiveKit video track is ready to display.
    // The <LiveAvatar> component watches isStreamReady to call session.attach().
    session.on(SessionEvent.SESSION_STREAM_READY, () => {
      setIsStreamReady(true);
    });

    session.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, () => {
      setIsAvatarTalking(true);
    });

    session.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, () => {
      setIsAvatarTalking(false);
    });

    session.start().catch((error: unknown) => {
      console.error('[LiveAvatarContextProvider] Failed to start HeyGen session:', error);
      // Also capture to Sentry via ChatLoggerContext so session start failures
      // are observable in production (console.error is invisible there).
      loggerRef.current.captureException(error);
    });

    return () => {
      // Null the ref first so any in-flight event handlers reading it are no-ops.
      sessionRef.current = null;
      setIsStreamReady(false);
      setIsAvatarTalking(false);
      setSessionState(SessionState.INACTIVE);

      session.stop().catch(() => {
        // Ignore stop errors on unmount — the session may have already ended.
      });
    };
  }, [sessionToken]);

  return (
    <LiveAvatarContext.Provider
      value={{ sessionRef, sessionState, isStreamReady, isAvatarTalking, ttsConfig, isMuted, toggleMute, pendingAudioTimeoutsRef }}
    >
      {children}
    </LiveAvatarContext.Provider>
  );
}
